import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Box,
  TextField,
  Typography,
  Divider,
  IconButton,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import CloseIcon from '@mui/icons-material/Close';
import ImageSelect from '../../../components/global/images/ImageSelector';
import { GalleryItem } from '../../../types';


interface GalleryImageDialogProps {
  open: boolean;
  onClose: () => void;
  onSave: (file: File | undefined, title?: string, description?: string) => void;
  initialItem?: GalleryItem;
  mode?: 'dialog' | 'inline';
  inlineVariant?: 'card' | 'embedded';
  onDelete?: () => void;
}

const SelectGalleryImageDialog: React.FC<GalleryImageDialogProps> = ({
  open,
  onClose,
  onSave,
  initialItem,
  mode = 'dialog',
  inlineVariant = 'card',
  onDelete,
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const [file, setFile] = useState<File | undefined>();
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const maxTitleLength = 30;
  const maxDescriptionLength = 200;


  useEffect(() => {
    setTitle(initialItem?.title || '');
    setDescription(initialItem?.description || '');
    setFile(undefined);
  }, [initialItem, open]);

  const handleSave = () => {
    onSave(
      file,
      title.trim() ? title.trim() : undefined,
      description.trim() ? description.trim() : undefined
    );
    onClose();
  };

  const trimmedTitle = title.trim();
  const trimmedDescription = description.trim();
  const initialTitle = initialItem?.title ?? '';
  const initialDescription = initialItem?.description ?? '';
  const isDirty = Boolean(file) || trimmedTitle !== initialTitle || trimmedDescription !== initialDescription;
  const canSave = initialItem ? isDirty : Boolean(file);

  const content = (
    <>
      <Box display="flex" justifyContent="center" sx={{ mb: 2 }}>
        <ImageSelect
          initialImagePath={initialItem?.imagePath}
          onImageChange={setFile}
          allowDelete
          enabled
          styleType="portrait"
          altText={initialItem ? "Gallery Image" : "New Gallery Image"}
        />
      </Box>
      <Box mb={2}>
        <TextField
          fullWidth
          label="Title"
          value={title}
          onChange={(e) => setTitle(e.target.value)}
          slotProps={{ htmlInput: { maxLength: maxTitleLength } }}
        />
        <Typography variant="caption" align="right" color={title.length > maxTitleLength ? 'error' : 'textSecondary'}>
          {title.length}/{maxTitleLength}
        </Typography>
      </Box>

      <Box mb={2}>
        <TextField
          fullWidth
          multiline
          rows={3}
          label="Description"
          value={description}
          slotProps={{ htmlInput: { maxLength: maxDescriptionLength } }}
          onChange={(e) => setDescription(e.target.value)}
        />
        <Typography variant="caption" align="right" color={description.length > maxDescriptionLength ? 'error' : 'textSecondary'}>
          {description.length}/{maxDescriptionLength}
        </Typography>
      </Box>
      {!file && !initialItem?.imagePath && (
        <Typography variant="caption" color="error">
          Image is required.
        </Typography>
      )}
    </>
  );

  const actions = (
    <DialogActions
      sx={{
        position: 'sticky',
        bottom: 0,
        bgcolor: 'background.paper',
        borderTop: '1px solid',
        borderColor: 'divider',
        px: 2,
        py: 1,
        m: 0,
        gap: 0,
        justifyContent: 'space-between',
        '& > :not(:first-of-type)': { ml: 0 },
        '& .MuiButton-root': { m: 0 },
      }}
    >
      <Button onClick={onClose} sx={{ flex: 1, borderRadius: '4px', py: 1.2, ml: 0 }}>
        Cancel
      </Button>
      <Button
        onClick={handleSave}
        variant="contained"
        sx={{ flex: 1, borderRadius: '4px', py: 1.2, ml: 0 }}
        disabled={!canSave}
      >
        Save
      </Button>
    </DialogActions>
  );

  if (mode === 'inline') {
    if (!open) return null;
    if (inlineVariant === 'embedded') {
      return (
        <Box>
          <Box sx={{ px: 2, pt: 0 }}>
            {content}
          </Box>
          <Box sx={{ px: 0, pb: 0, mb: 0 }}>
            {actions}
          </Box>
        </Box>
      );
    }
    return (
      <Box sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2, p: 2 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">{initialItem ? 'Edit Gallery Image' : 'Add Gallery Image'}</Typography>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
        <Divider sx={{ my: 1 }} />
        {content}
        {actions}
      </Box>
    );
  }

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="sm" fullScreen={isMobile}>
      <DialogTitle>
        <Box display="flex" alignItems="center" justifyContent="space-between">
          <Box sx={{ width: 40, display: 'flex', justifyContent: 'flex-start' }} />
          <Typography variant="h6" sx={{ flex: 1, textAlign: 'center' }}>
            {initialItem ? 'Edit Gallery Image' : 'Add Gallery Image'}
          </Typography>
          <IconButton onClick={onClose} aria-label="Close">
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      <Divider />
      <DialogContent>
        {content}
      </DialogContent>
      {actions}
    </Dialog>
  );
};

export default SelectGalleryImageDialog;
